///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type cube } from "@stovokor/math/cube"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * A solid unit UV sphere rendered as triangles, used by the ember dome
 * fresnel marker. Normals are derived in the shader from the positions
 * (unit sphere), so position is the only attribute.
 */
export class EmberSphereGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public transparency = true
	public available: boolean = true
	private undeletable: boolean

	constructor(context: StovokorContext, undeletable: boolean = false, latSegments: number = 16, lonSegments: number = 24) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		this.undeletable = undeletable

		for(let lat = 0; lat <= latSegments; lat++) {
			const phi = (lat / latSegments) * Math.PI - Math.PI / 2
			const y = Math.sin(phi)
			const ringRadius = Math.cos(phi)
			for(let lon = 0; lon <= lonSegments; lon++) {
				const theta = (lon / lonSegments) * Math.PI * 2
				this.position.addPos(
					Math.cos(theta) * ringRadius,
					y,
					Math.sin(theta) * ringRadius
				)
			}
		}

		const stride = lonSegments + 1
		for(let lat = 0; lat < latSegments; lat++) {
			for(let lon = 0; lon < lonSegments; lon++) {
				const a = lat * stride + lon
				const b = a + stride
				this.indexBuffer.quad(a, b, b + 1, a + 1)
			}
		}

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		if(this.undeletable) {
			return
		}
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

// Shared unit sphere geometry, one per GL context
const sharedCache = new WeakMap<StovokorContext, EmberSphereGeometry>()

export function sharedEmberSphereGeometry(context: StovokorContext): EmberSphereGeometry {
	let geometry = sharedCache.get(context)
	if(!geometry) {
		geometry = new EmberSphereGeometry(context, true)
		sharedCache.set(context, geometry)
	}
	return geometry
}
